import { filter, forEach, get, groupBy, map, property, reduce, cloneDeep } from 'lodash';
import { createSelector } from 'reselect';
import Big from 'big.js';
import { objectSelectorFactory } from 'utils/selectors/selectorFactories';

const ndfl2Slice = property('userIncomes.ndfl2');
const incomeInformationsSlice = property('userIncomes.incomeInformations');
const insuranceTaxesSlice = property('userIncomes.insuranceTaxes');
const percentDepositsSlice = property('userIncomes.percentDeposits');
const tabsStatusSlice = property('userIncomes.userIncomes');

export const ndfl2Selector = objectSelectorFactory(ndfl2Slice);
export const incomeInformationsSelector = objectSelectorFactory(incomeInformationsSlice);
export const insuranceTaxesSelector = objectSelectorFactory(insuranceTaxesSlice);
export const percentDepositsSelector = objectSelectorFactory(percentDepositsSlice);
export const tabsStatusSelector = objectSelectorFactory(tabsStatusSlice);

export const filterValueSelector = createSelector(
  tabsStatusSlice,
  (object) => object && object.get('filter'),
);
export const groupedNdfl2Selector = createSelector(
  [ndfl2Selector, filterValueSelector],
  ({ data }, filterValue) => data && groupNdlf2(data, filterValue),
);
export const groupedDeclarationIncomesSelector = createSelector(
  [incomeInformationsSelector, filterValueSelector],
  ({ data }, filterValue) => data && groupDeclarationIncomes(data, filterValue),
);
export const groupedInsuranceTaxesSelector = createSelector(
  [insuranceTaxesSelector, filterValueSelector],
  ({ data }, filterValue) => data && groupInsuranceTaxes(data, filterValue),
);

export const percentDepositsDataSelector = createSelector(
  [percentDepositsSelector, filterValueSelector],
  ({ data }, filterValue) => data && percentDepositsFiltered(data, filterValue),
);

export const percentDepositsYearsSelector = createSelector(
  [percentDepositsSelector],
  ({ data }) => data && percentDepositsYears(data),
);

export const percentDepositsBanksGroupedByYearsSelector = createSelector(
  [percentDepositsSelector],
  ({ data }) => data && percentDepositsBanksGroupedByYears(data),
);

function percentDepositsBanksGroupedByYears(data) {
  return data.reduce((acc, item) => {
    const cloned = cloneDeep(item);

    acc[item.year] = cloned.banks;

    return acc;
  }, {});
}

function percentDepositsYears(data) {
  return data.map((item) => item.year);
}

function percentDepositsFiltered(data, filterValue) {
  const field = /^\d+$/.test(filterValue) ? 'inn' : 'name';

  return data.reduce((acc, item) => {
    const cloned = cloneDeep(item);
    const banks = cloned.banks.filter((bank) => {
      return -1 !== get(bank, field, '').toLowerCase().indexOf(filterValue.toLowerCase());
    });

    if (banks.length === 0) {
      return acc;
    }

    cloned.banks = banks;
    acc.push(cloned);

    return acc;
  }, []);
}

function groupInsuranceTaxes(data, filterValue) {
  const filteredValues = filterByCompany(data, filterValue);
  const groupedByYear = groupBy(filteredValues, 'year');
  const yearOrder = [...new Set(map(filteredValues, ({ year }) => year))];
  forEach(groupedByYear, (yearGroup, key) => {
    const groupedByCompany = groupBy(yearGroup, ({ inn, kpp }) => `${inn}|${kpp}`);
    const companyOrder = [...new Set(map(yearGroup, ({ inn, kpp }) => `${inn}|${kpp}`))];
    const yearInsuranceTaxes = calcProperty(yearGroup, 'payment');
    groupedByYear[key] = {
      groupedByCompany,
      companyOrder,
      yearInsuranceTaxes,
    };
  });
  return {
    groupedByYear,
    yearOrder,
  };
}

function groupDeclarationIncomes(data, filterValue) {
  const filteredValues = filterByCompany(data, filterValue);
  const groupedByYear = groupBy(filteredValues, 'year');
  const yearOrder = [...new Set(map(filteredValues, ({ year }) => year))];
  forEach(groupedByYear, (yearGroup, key) => {
    const groupedByCompany = groupBy(yearGroup, ({ inn, kpp }) => `${inn}|${kpp}`);
    const companyOrder = [...new Set(map(yearGroup, ({ inn, kpp }) => `${inn}|${kpp}`))];
    const yearIncome = calcProperty(yearGroup, 'income');
    const yearDeducted = calcProperty(yearGroup, 'deducted');
    groupedByYear[key] = {
      groupedByCompany,
      companyOrder,
      yearIncome,
      yearDeducted,
    };
  });
  return {
    groupedByYear,
    yearOrder,
  };
}

function groupNdlf2(data, filterValue) {
  const filteredValues = filterByCompany(data, filterValue);
  const groupedByYear = groupBy(filteredValues, 'year');
  const yearOrder = [...new Set(map(filteredValues, ({ year }) => year))];
  forEach(groupedByYear, (yearGroup, key) => {
    const groupedByCompany = groupBy(yearGroup, ({ inn, kpp }) => `${inn}|${kpp}`);
    const companyOrder = [...new Set(map(yearGroup, ({ inn, kpp }) => `${inn}|${kpp}`))];
    const quantity = new Set(map(yearGroup, ({ documentId }) => documentId)).size;
    const yearIncome = calcProperty(yearGroup, 'income');
    const yearTax = calcProperty(yearGroup, 'tax');
    groupedByYear[key] = {
      groupedByCompany,
      companyOrder,
      yearIncome,
      quantity,
      yearTax,
    };
  });
  return {
    groupedByYear,
    yearOrder,
  };
}

export function calcProperty(data, propertyName) {
  return reduce(
    data,
    (accum = Big(0), item) => accum.plus(get(item, propertyName, 0) || 0),
    Big(0),
  ).toString();
}

function filterByCompany(data, filterValue) {
  return filter(
    data,
    (item) => -1 !== get(item, 'companyName', '').toLowerCase().indexOf(filterValue),
  );
}
